import { Link, createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  CheckCircle2,
  Server,
  Smartphone,
  XCircle,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { LoadingSpinner } from '@/components/loading-spinner'
import { useDispatcherAgents } from '@/lib/api/queries'
import { useSetAgentEnabled } from '@/lib/api/mutations'
import { localizeDigits } from '@/lib/localize-digits'

export const Route = createFileRoute('/settings/dispatcher')({
  component: VehicleFetchPage,
})

function VehicleFetchPage() {
  const { t } = useTranslation()

  const {
    data: agentsData,
    isLoading: agentsLoading,
    error: agentsError,
  } = useDispatcherAgents()

  const setAgentEnabled = useSetAgentEnabled()

  const agents = agentsData?.agents ?? []
  const enabledCount = agents.filter((a) => a.enabled).length
  const totalRequests = agents.reduce((sum, a) => sum + a.requests_processed, 0)

  const handleToggleAgent = async (agentId: string, enabled: boolean) => {
    try {
      await setAgentEnabled.mutateAsync({ agentId, enabled })
      toast.success(t('vehicleFetch.dispatcher.enableSuccess'))
    } catch {
      toast.error(t('vehicleFetch.dispatcher.enableError'))
    }
  }

  return (
    <div>
      {/* Fixed Header */}
      <div className="fixed-header">
        <div className="container mx-auto max-w-screen-md px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/settings">
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              </Link>
              <div>
                <h1 className="text-xl font-bold">{t('vehicleFetch.title')}</h1>
                <p className="text-xs text-muted-foreground">
                  {t('vehicleFetch.description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="container mx-auto max-w-screen-md px-4 content-with-fixed-header pb-nav-and-safe-area">
        <div className="space-y-4 pt-4">
          {/* Loading */}
          {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
          {agentsLoading && !agentsData && (
            <Card>
              <CardContent className="py-12">
                <LoadingSpinner label={t('vehicleFetch.overview.loading')} />
              </CardContent>
            </Card>
          )}

          {/* Error */}
          {agentsError && (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-3 text-center">
                  <Server className="h-8 w-8 text-destructive" />
                  <p className="font-medium text-destructive">
                    {t('vehicleFetch.overview.error')}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Card 1 — Overview */}
          {agentsData && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t('vehicleFetch.overview.title')}
                </CardTitle>
                <CardDescription>
                  {t('vehicleFetch.overview.description')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-x-4 gap-y-3 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">
                      {t('vehicleFetch.overview.agents')}
                    </p>
                    <p className="font-medium">
                      {localizeDigits(agentsData.total_count)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">
                      {t('vehicleFetch.overview.enabled')}
                    </p>
                    <p className="font-medium">
                      {localizeDigits(enabledCount)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">
                      {t('vehicleFetch.overview.requests')}
                    </p>
                    <p className="font-medium">
                      {localizeDigits(totalRequests)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Card 2 — Dispatcher Agents */}
          {agentsData && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t('vehicleFetch.dispatcher.title')}
                </CardTitle>
                <CardDescription>
                  {t('vehicleFetch.dispatcher.description')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {agents.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {t('vehicleFetch.dispatcher.noAgents')}
                  </p>
                )}
                {agents.map((agent) => (
                  <div
                    key={agent.agent_id}
                    className="flex items-center gap-3 rounded-md border p-3 text-sm"
                  >
                    <Switch
                      checked={agent.enabled}
                      onCheckedChange={(checked) =>
                        handleToggleAgent(agent.agent_id, checked)
                      }
                      disabled={setAgentEnabled.isPending}
                      aria-label={
                        agent.enabled
                          ? t('vehicleFetch.dispatcher.enabled')
                          : t('vehicleFetch.dispatcher.disabled')
                      }
                      className="shrink-0"
                    />
                    {agent.agent_type === 'mobile' ? (
                      <Smartphone className="h-3.5 w-3.5 text-purple-500 shrink-0" />
                    ) : (
                      <Server className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate font-mono text-xs">
                        {agent.agent_id}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {agent.hostname}
                        {agent.ipv6_addresses.length > 0 &&
                          ` · ${localizeDigits(agent.ipv6_addresses.length)} IPs`}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {t('vehicleFetch.dispatcher.lastSeen')}:{' '}
                        {localizeDigits(
                          new Date(agent.last_seen).toLocaleTimeString(),
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline" className="text-xs">
                        {agent.agent_type === 'mobile'
                          ? t('vehicleFetch.dispatcher.mobile')
                          : t('vehicleFetch.dispatcher.server')}
                      </Badge>
                      {agent.enabled ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span className="text-xs text-muted-foreground">
                        {localizeDigits(agent.requests_processed)}
                        {t('vehicleFetch.dispatcher.requests')}
                      </span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
